// src/utils/calculationUtils.js
import { gsPayTables } from '../data/gsPayTables';
import { localityAdjustments } from '../data/localityAdjustments';
import { calculateFersSupplement, calculateVeraFersSupplement, isEligibleForSupplement } from './supplementCalculator';

// Calculate the high-3 average salary (highest 3 annual salaries)
export const calculateHigh3Salary = (salaryData) => {
  // Filter out invalid/zero salaries and make a copy to avoid mutating original
  const validSalaries = salaryData.filter(salary => salary && typeof salary === 'number' && salary > 0);

  // Handle cases with fewer than 3 data points
  if (validSalaries.length === 0) {
    return 0;
  }
  if (validSalaries.length < 3) {
    // Average the available salaries if fewer than 3
    const total = validSalaries.reduce((sum, salary) => sum + salary, 0);
    return Math.round(total / validSalaries.length);
  }

  // Sort salaries in descending order
  validSalaries.sort((a, b) => b - a);

  // Take the top 3 salaries
  const top3Salaries = validSalaries.slice(0, 3);

  // Calculate the average
  const total = top3Salaries.reduce((sum, salary) => sum + salary, 0);
  return Math.round(total / 3);
};

// Calculate high-3 up to a specific year (useful for deferred retirement)
export const calculateHigh3SalaryUpToYear = (salaryData, years, targetYear) => {
  if (!salaryData || !years || !targetYear) {
    return 0;
  }
  
  // Get all salaries up to and including the target year
  const validSalaries = [];
  for (let i = 0; i < years.length; i++) {
    if (years[i] <= targetYear && salaryData[i] && salaryData[i] > 0) {
      validSalaries.push(salaryData[i]);
    }
  }
  
  // Now calculate high-3 using only these salaries
  if (validSalaries.length === 0) {
    return 0;
  }
  if (validSalaries.length < 3) {
    // Average the available salaries if fewer than 3
    const total = validSalaries.reduce((sum, salary) => sum + salary, 0);
    return Math.round(total / validSalaries.length);
  }
  
  // Sort salaries in descending order
  validSalaries.sort((a, b) => b - a);
  
  // Take the top 3 salaries
  const top3Salaries = validSalaries.slice(0, 3);
  
  // Calculate the average
  const total = top3Salaries.reduce((sum, salary) => sum + salary, 0);
  return Math.round(total / 3);
};

// Calculate salary data for a scenario
export const calculateSalaryData = (years, currentGrade, currentStep, locality, retirementYear, promotions, includeColaForSalary, annualCola, departureYear, showColaWhileEmployed = true) => {
  const currentYear = new Date().getFullYear();
  const salaries = [];
  const stepProgression = []; // Track step progression by year
  const gradeProgression = []; // Track grade progression by year
  
  let grade = currentGrade;
  let step = currentStep;
  let timeSinceLastStep = 1; // Start at 1 to align with federal timing (first year counts)
  
  for (const year of years) {
    // After retirement or departure (for deferred), salary is 0
    if (year >= retirementYear || (departureYear && year >= departureYear)) {
      salaries.push(0);
      stepProgression.push(0);
      gradeProgression.push(0);
      continue;
    }
    
    // Apply promotions
    const promotion = promotions.find(p => p.year === year);
    if (promotion) {
      const oldGrade = grade;
      grade = promotion.grade;
      step = calculateNewStepAfterPromotion(oldGrade, step, grade); // Fix: Pass oldGrade as currentGrade
      timeSinceLastStep = 1; // Reset to 1 (not 0) so the first year counts
    }
    
    // Calculate salary with locality adjustment
    let adjustedSalary;
    
    if (grade === 16) { // Special handling for SES
      // Use the salary cap as SES base salary
      adjustedSalary = gsPayTables.salaryCap;
    } else {
      const baseSalary = gsPayTables.base[grade][step - 1];
      const localityRate = localityAdjustments[locality] || 1;
      adjustedSalary = Math.round(baseSalary * localityRate);
      
      // Apply salary cap
      if (adjustedSalary > gsPayTables.salaryCap) {
        adjustedSalary = gsPayTables.salaryCap;
      }
    }
    
    // Apply COLA if enabled and showColaWhileEmployed is true
    if (includeColaForSalary && year > currentYear && showColaWhileEmployed) {
      const yearsSinceStart = year - currentYear;
      adjustedSalary = Math.round(adjustedSalary * Math.pow(1 + (annualCola / 100), yearsSinceStart));
      
      // Re-apply salary cap after COLA (if COLA is set, cap may increase with inflation)
      // For now, keeping static cap as per OPM rules
      if (adjustedSalary > gsPayTables.salaryCap) {
        adjustedSalary = gsPayTables.salaryCap;
      }
    }
    
    salaries.push(adjustedSalary);
    stepProgression.push(step);
    gradeProgression.push(grade);
    
    // Step increases logic - aligned with federal timing
    if (step < 10) {
      // Store the original step for logging purposes
      const originalStep = step;
      let shouldIncrease = false;
      
      // Steps 1-3: Increase after 1 year 
      if (step < 4 && timeSinceLastStep >= 1) {
        shouldIncrease = true;
      } 
      // Steps 4-6: Increase after 2 years
      else if (step >= 4 && step < 7 && timeSinceLastStep >= 2) {
        shouldIncrease = true;
      } 
      // Steps 7-9: Increase after 3 years
      else if (step >= 7 && step < 10 && timeSinceLastStep >= 3) {
        shouldIncrease = true;
      }
      
      if (shouldIncrease) {
        step++;
        // Log step increase for debugging
        if (originalStep !== step) {
          // console.log(`Year ${year}: Step increased from ${originalStep} to ${step}`);
        }
        timeSinceLastStep = 1; // Reset to 1 (not 0) so the first year counts
      } else {
        timeSinceLastStep++; // Increment time since last step
      }
    } else {
      timeSinceLastStep++; // Increment time counter even at step 10
    }
  }
  
  return {
    salaries,
    stepProgression,
    gradeProgression
  };
};

// Calculate steps for new promotions - using the "two-step rule"
export const calculateNewStepAfterPromotion = (currentGrade, currentStep, newGrade) => {
  // Handle SES special case - SES doesn't have steps
  if (currentGrade === 'SES' || newGrade === 'SES') {
    // For SES positions, there are no steps, so return 1 as a default
    return 1;
  }
  
  if (newGrade <= currentGrade) return 1; // If it's not a promotion, start at step 1
  
  // Check if we have data for these grades in the pay tables
  if (!gsPayTables.base[currentGrade] || !gsPayTables.base[newGrade]) {
    console.warn(`Missing pay table data for grade ${currentGrade} or ${newGrade}. Defaulting to step 1.`);
    return 1;
  }
  
  let targetSalary;
  
  if (currentStep === 10) {
    // Special handling for step 10: sum the last two step increases and add to step 10 salary
    const step8Salary = gsPayTables.base[currentGrade][7]; // Index 7 is step 8
    const step9Salary = gsPayTables.base[currentGrade][8]; // Index 8 is step 9
    const step10Salary = gsPayTables.base[currentGrade][9]; // Index 9 is step 10
    
    const step8to9Increase = step9Salary - step8Salary;
    const step9to10Increase = step10Salary - step9Salary;
    const combinedIncrease = step8to9Increase + step9to10Increase;
    
    targetSalary = step10Salary + combinedIncrease;
  } else {
    // Standard two-step rule calculation for steps 1-9
    const currentGradeSalary = gsPayTables.base[currentGrade][currentStep - 1];
    const twoStepHigherSalary = gsPayTables.base[currentGrade][Math.min(currentStep + 1, 9) - 1];
    const promotionStepIncrease = twoStepHigherSalary - currentGradeSalary;
    
    targetSalary = currentGradeSalary + promotionStepIncrease;
  }
  
  // Find the step in the new grade that's closest to but not less than the calculated salary
  let newStep = 1;
  
  for (let i = 0; i < 10; i++) {
    if (gsPayTables.base[newGrade][i] >= targetSalary) {
      newStep = i + 1;
      break;
    }
  }
  
  // If we couldn't find a step that meets the target, use step 10
  if (newStep === 1 && targetSalary > gsPayTables.base[newGrade][0]) {
    newStep = 10;
  }
  
  return newStep;
};

// Calculate pension data for a scenario
export const calculatePensionData = (salaryData, years, yearOfBirth, retirementYear, pensionMultiplier, yearsOfService, retirementType, socialSecurityData, bioData = {}, estimatedHighThree = 0, departureYear = null, deferredRetirementAge = null, showAnnualCola = true) => {
  // Add the sick leave service credit if provided and the pension type is eligible
  let totalYearsOfService = yearsOfService;
  
  // Only add sick leave credit for immediate retirements (not for deferred)
  if (retirementType !== 'deferred' && bioData.projectedSickLeave) {
    // Convert sick leave hours to years of service (2087 hours = 1 year)
    const sickLeaveYears = bioData.projectedSickLeave / 2087;
    totalYearsOfService += sickLeaveYears;
  }
  const pensions = [];
  
  // Skip if years of service is not available yet
  if (!totalYearsOfService) {
    return years.map(() => 0);
  }
  
  // Calculate base pension amount
  let annualPension;
  const pensionType = retirementType || 'standard'; // Default to standard FERS
  const retireAge = yearOfBirth ? (retirementYear - yearOfBirth) : 0; // Original retirement age
  
  // Calculate the age when the deferred pension actually starts
  const actualPensionStartAge = pensionType === 'deferred' ? deferredRetirementAge : retireAge;
  
  if (pensionType === 'standard') {
    // Standard FERS: 1% per year, or 1.1% per year if retiring at age 62+ with 20+ years
    const multiplier = (actualPensionStartAge >= 62 && totalYearsOfService >= 20) ? 0.011 : 0.01;
    annualPension = estimatedHighThree * multiplier * totalYearsOfService;
  } 
  else if (pensionType === 'vera') {
    // VERA: Uses the standard FERS formula (1% per year of service)
    // No age reduction penalty under FERS for VERA
    annualPension = estimatedHighThree * 0.01 * totalYearsOfService;
    
    // Log VERA calculation details
    // console.log('--- VERA Pension Calculation ---');
    // console.log('High Three:', estimatedHighThree);
    // console.log('Years of Service:', totalYearsOfService);
    // console.log('Annual Pension Amount:', annualPension);
  }
  else if (pensionType === 'special') {
    // Special FERS: 1.7% per year for first 20 years, 1.1% for additional years
    if (totalYearsOfService <= 20) {
      annualPension = estimatedHighThree * 0.017 * totalYearsOfService;
    } else {
      annualPension = estimatedHighThree * (0.017 * 20 + 0.011 * (totalYearsOfService - 20));
    }
  } 
  else if (pensionType === 'deferred') {
    // Deferred FERS: Always 1% per year
    // For deferred retirement, years of service is calculated based on EOD and departure year.
    let effectiveYearsOfService = totalYearsOfService; // Use passed-in years of service as default
    
    // Debug info removed for cleaner output
    
    if (departureYear && bioData.entryOnDuty) {
      const eodDate = new Date(bioData.entryOnDuty);
      if (!isNaN(eodDate.getFullYear()) && eodDate.getFullYear() >= 1900) {
        // Calculate full years between EOD and departure year
        let calculatedYears = departureYear - eodDate.getFullYear();
        
        // Adjust for month/day if we have precise dates
        if (bioData.preciseServiceComputation) {
          calculatedYears = bioData.preciseServiceComputation.totalYears;
        }
        
        // Safety check and use calculated years if valid
        if (calculatedYears > 0) {
          effectiveYearsOfService = calculatedYears;
        }

      } else {
        console.warn('Deferred Pension: Invalid EOD date, using provided years of service:', totalYearsOfService);
      }
    } else {
      console.warn('Deferred Pension: Missing departureYear or EOD, using provided years of service:', totalYearsOfService);
    }
    
    // Calculate high-3 based on salary data up to departure year
    // This ensures both past and future separations get correct high-3
    let highThreeAtSeparation = estimatedHighThree;
    
    // If we have salary data and departure year, recalculate high-3 up to departure
    if (salaryData && salaryData.length > 0 && years && years.length > 0 && departureYear) {
      // Use the custom function to calculate high-3 up to departure year
      const calculatedHigh3 = calculateHigh3SalaryUpToYear(salaryData, years, departureYear);
      
      // Only use calculated value if it's valid
      if (calculatedHigh3 > 0) {
        highThreeAtSeparation = calculatedHigh3;
      }
    }
    
    // Calculate the base pension amount
    let baseAnnualPension = highThreeAtSeparation * 0.01 * effectiveYearsOfService;
    
    // Deferred pension calculation complete

    annualPension = baseAnnualPension; // Assign base pension

    // Apply age reduction if taking deferred retirement before age 62
    // Use the actual pension start age for this calculation
    if (actualPensionStartAge && actualPensionStartAge < 62) {
      const yearsUnder62 = 62 - actualPensionStartAge;
      const reductionFactor = yearsUnder62 * 0.05; // 5% per year
      // Apply age reduction calculation
      annualPension = annualPension * (1 - reductionFactor);
    } else {
      // No age reduction needed
    }
    
    // If we don't have a valid deferred retirement age, default to 62 for calculation purposes
    if (!deferredRetirementAge || deferredRetirementAge < 57) {
      console.warn('Invalid or missing deferredRetirementAge, defaulting to 62 for calculation');
      // Don't modify the calculation, just warn - the UI should handle setting a proper age
    }
  }
  else {
    // Default to standard FERS if pension type is invalid
    annualPension = estimatedHighThree * 0.01 * yearsOfService;
  }
  
  // Apply survivor benefit reduction if any
  const survivorBenefit = 0; // Default to 0, update this value when survivor benefits are implemented
  if (survivorBenefit > 0) {
    // 5% reduction for 25% survivor benefit, 10% reduction for 50% survivor benefit
    const reduction = survivorBenefit === 25 ? 0.05 : survivorBenefit === 50 ? 0.10 : 0;
    annualPension = annualPension * (1 - reduction);
  }
  
  // Get annual COLA (default to 0 if not specified)
  const annualCola = 2.0; // Default COLA percentage
  
  // For deferred retirement, calculate the actual retirement year based on birth year and the deferred retirement age
  let effectiveRetirementYear = retirementYear;
  if (pensionType === 'deferred' && yearOfBirth) {
    // Set default deferred retirement age to 62 if not provided or invalid
    const defaultDeferredAge = 62;
    const retirementAge = (deferredRetirementAge && deferredRetirementAge >= 57) ? 
      deferredRetirementAge : defaultDeferredAge;
    
    effectiveRetirementYear = yearOfBirth + retirementAge;
    // Calculate effective retirement year for deferred pension
  }
  
  // Always ensure we have a valid retirement year for deferred pensions
  // For deferred pensions, the effective retirement year can be before, during, or after departure
  if (pensionType === 'deferred' && !effectiveRetirementYear) {
    console.warn('Missing effective retirement year for deferred pension, using fallback calculation');
    effectiveRetirementYear = Math.max(departureYear + 1, new Date().getFullYear() + 1);
    // Using fallback retirement year calculation
  }
  
  for (const year of years) {
    if (pensionType === 'deferred') {
      // For deferred, pension starts at the deferred retirement age (effectiveRetirementYear), not departure year
      if (year >= effectiveRetirementYear) {
        if (showAnnualCola) {
          // Calculate COLA-adjusted pension
          const yearsSinceRetirement = year - effectiveRetirementYear;
          const adjustedPension = annualPension * Math.pow(1 + (annualCola / 100), yearsSinceRetirement);
          pensions.push(Math.round(adjustedPension));
        } else {
          // If COLA is disabled, pension remains the same
          pensions.push(Math.round(annualPension));
        }
      } else {
        pensions.push(0); // No pension before reaching deferred retirement age
      }
    } else {
      // Standard or special pension
      if (year >= retirementYear) {
        if (showAnnualCola) {
          const yearsSinceRetirement = year - retirementYear;
          const adjustedPension = annualPension * Math.pow(1 + (annualCola / 100), yearsSinceRetirement);
          pensions.push(Math.round(adjustedPension));
        } else {
          // If COLA is disabled, pension remains the same
          pensions.push(Math.round(annualPension));
        }
      } else {
        pensions.push(0); // No pension before retirement
      }
    }
  }
  
  // Return just the pension data
  return pensions;
};  // End of calculatePensionData function

// Calculate FERS supplement if eligible
// REMOVED DUPLICATE FUNCTION

// Check if eligible for FERS supplement
export const calculateSupplementEligibility = (retirementAge, yearsOfService, retirementType, high3Salary = 0, pensionData = []) => {
  const isEligible = isEligibleForSupplement(retirementAge, yearsOfService, retirementType);
  
  if (isEligible) {
    // We'll just return the eligibility status and parameters needed for later calculations
    return {
      isEligible: true,
      high3Salary,
      pensionData,
    };
  } else {
    // Not eligible
    return {
      isEligible: false,
      high3Salary,
      pensionData,
    };
  }
};

// Helper function to estimate current salary
export const calculateEstimatedSalary = (currentGrade, currentStep, locality) => {
  const baseSalary = gsPayTables.base[currentGrade][currentStep - 1];
  const localityRate = localityAdjustments[locality] || 1;
  const adjustedSalary = Math.round(baseSalary * localityRate);
  
  // Apply salary cap
  if (adjustedSalary > gsPayTables.salaryCap) {
    return gsPayTables.salaryCap;
  }
  
  return adjustedSalary;
};

// Calculate TSP data for a scenario
export const calculateTspData = (years, currentBalance, currentGrade, retirementYear, withdrawalRate, preRetirementGrowth, postRetirementGrowth, departureYear, tspContribution, agencyMatch, bioData, tspWithdrawalStartAge) => {
  const tspBalances = [];
  
  let balance = currentBalance;
  const estimatedSalary = calculateEstimatedSalary(currentGrade, bioData.currentStep, bioData.locality);
  
  // Calculate withdrawal start year based on tspWithdrawalStartAge if provided
  let withdrawalStartYear = null;
  if (tspWithdrawalStartAge && bioData.dateOfBirth) {
    const birthYear = new Date(bioData.dateOfBirth).getFullYear();
    withdrawalStartYear = birthYear + tspWithdrawalStartAge;
  }
  
  // If no valid withdrawal start age is provided, default to retirement year
  const effectiveWithdrawalStartYear = withdrawalStartYear || retirementYear;
  
  for (const year of years) {
    // For deferred retirement, contributions stop at departure year
    const effectiveRetirementYear = departureYear ? Math.min(departureYear, retirementYear) : retirementYear;
    
    if (year < effectiveRetirementYear) {
      // Pre-retirement: contributions + growth
      const employeeContribution = estimatedSalary * (tspContribution / 100);
      const agencyContribution = estimatedSalary * (agencyMatch / 100);
      
      const totalContribution = employeeContribution + agencyContribution;
      
      // Growth on existing balance + new contributions (assuming even distribution throughout year)
      balance = balance * (1 + preRetirementGrowth / 100) + totalContribution * (1 + preRetirementGrowth / 200);
    } else if (year < effectiveWithdrawalStartYear) {
      // Post-retirement but before withdrawal start age: growth only, no withdrawals
      balance = balance * (1 + postRetirementGrowth / 100);
    } else {
      // Post-retirement and at or after withdrawal start age: withdrawals + growth
      const withdrawal = year === effectiveWithdrawalStartYear 
        ? 0 // No withdrawal in first year of withdrawal period
        : balance * (withdrawalRate / 100);
      
      // Growth minus withdrawals
      balance = balance * (1 + postRetirementGrowth / 100) - withdrawal;
      
      // Don't allow negative balance
      balance = Math.max(0, balance);
    }
    
    tspBalances.push(Math.round(balance));
  }
  
  return tspBalances;
};

// Calculate NPV data for a scenario
export const calculateNpvData = (years, salaryData, pensionData, tspData, withdrawalRate, discountRate, taxRate, showAfterTax, supplementData, socialSecurityData, additionalIncomeData = []) => {
  const salaryNpv = [];
  const pensionNpv = [];
  const tspNpv = [];
  const supplementNpv = [];
  const socialSecurityNpv = [];
  const additionalIncomeNpv = []; // For additional income
  const yearlyNpv = []; // Individual year NPV
  const cumulativeNpv = []; // Running total NPV
  
  let totalNpv = 0;
  
  const baseYear = years[0];
  
  for (let i = 0; i < years.length; i++) {
    const year = years[i];
    const salary = salaryData[i] || 0;
    const pension = pensionData[i] || 0;
    const tspBalance = tspData[i] || 0;
    const supplement = supplementData && supplementData[i] ? supplementData[i] : 0;
    const socialSecurity = socialSecurityData && socialSecurityData[i] ? socialSecurityData[i] : 0;
    const additionalIncome = additionalIncomeData && additionalIncomeData[i] ? additionalIncomeData[i] : 0;
    
    // Apply tax adjustment if requested
    const taxFactor = showAfterTax ? (1 - taxRate / 100) : 1;
    
    // Number of years from base year
    const t = year - baseYear;
    const discountFactor = 1 / Math.pow(1 + (discountRate / 100), t);
    
    // Calculate NPV for each component
    const salaryNpvValue = salary * taxFactor * discountFactor;
    const pensionNpvValue = pension * taxFactor * discountFactor;
    
    // TSP withdrawal (using provided rate)
    const tspWithdrawal = tspBalance * (withdrawalRate / 100);
    const tspNpvValue = tspWithdrawal * taxFactor * discountFactor;
    
    // Social Security and Supplement NPV
    const supplementNpvValue = supplement * taxFactor * discountFactor;
    const socialSecurityNpvValue = socialSecurity * taxFactor * discountFactor;
    const additionalIncomeNpvValue = additionalIncome * taxFactor * discountFactor;
    
    salaryNpv.push(salaryNpvValue);
    pensionNpv.push(pensionNpvValue);
    tspNpv.push(tspNpvValue);
    supplementNpv.push(supplementNpvValue);
    socialSecurityNpv.push(socialSecurityNpvValue);
    additionalIncomeNpv.push(additionalIncomeNpvValue);
    
    // Add to total NPV - using either salary or pension (not both) plus TSP, supplement and Social Security
    const incomeNpv = Math.max(salaryNpvValue, pensionNpvValue); // Either salary or pension, not both
    const yearTotal = incomeNpv + tspNpvValue + supplementNpvValue + socialSecurityNpvValue + additionalIncomeNpvValue;
    yearlyNpv.push(yearTotal);
    
    // Update running total
    totalNpv += yearTotal;
    cumulativeNpv.push(totalNpv);
  }
  
  return {
    salaryNpv,
    pensionNpv,
    tspNpv,
    supplementNpv,
    socialSecurityNpv,
    additionalIncomeNpv, // Add new NPV data here
    yearlyNpv,
    cumulativeNpv,
    totalNpv
  };
};

// Utility function to calculate Net Present Value for a single year's cash flow
export const calculateNPV = (cashFlow, year, discountRate, startYear) => {
  if (discountRate === 0) {
    return cashFlow; // If discount rate is 0, NPV is just the cash flow
  }
  const rate = discountRate / 100;
  const yearsElapsed = year - startYear;
  // Ensure yearsElapsed is not negative if year < startYear (shouldn't happen in typical flow)
  if (yearsElapsed < 0) return 0; 
  
  // NPV formula for a single future cash flow: CF / (1 + r)^n
  const npv = cashFlow / Math.pow(1 + rate, yearsElapsed);
  return Math.round(npv);
};

// Helper function to get full retirement age based on birth year
const getFullRetirementAge = (birthYear) => {
  if (birthYear <= 1937) return 65;
  if (birthYear === 1938) return 65 + 2/12;
  if (birthYear === 1939) return 65 + 4/12;
  if (birthYear === 1940) return 65 + 6/12;
  if (birthYear === 1941) return 65 + 8/12;
  if (birthYear === 1942) return 65 + 10/12;
  if (birthYear >= 1943 && birthYear <= 1954) return 66;
  if (birthYear === 1955) return 66 + 2/12;
  if (birthYear === 1956) return 66 + 4/12;
  if (birthYear === 1957) return 66 + 6/12;
  if (birthYear === 1958) return 66 + 8/12;
  if (birthYear === 1959) return 66 + 10/12;
  if (birthYear >= 1960) return 67;
  return 67; // Default for unknown years
};

// Helper function to get Social Security adjustment factor
const getSocialSecurityAdjustmentFactor = (startAge, fullRetirementAge) => {
  if (startAge < fullRetirementAge) {
    const monthsEarly = (fullRetirementAge - startAge) * 12;
    let reduction = 0;
    if (monthsEarly <= 36) {
      reduction = monthsEarly * (5 / 9 / 100);
    } else {
      reduction = (36 * (5 / 9 / 100)) + ((monthsEarly - 36) * (5 / 12 / 100));
    }
    return 1 - reduction;
  } else if (startAge > fullRetirementAge) {
    const monthsDelayed = (startAge - fullRetirementAge) * 12;
    // Delayed retirement credits apply up to age 70
    const applicableMonthsDelayed = Math.min(monthsDelayed, (70 - fullRetirementAge) * 12);
    // Credit rate depends on birth year, typically 8% per year (2/3% per month) for those born 1943 or later
    // For simplicity, assuming 8% per year for this example
    const creditPerMonth = (8 / 100) / 12;
    return 1 + (applicableMonthsDelayed * creditPerMonth);
  }
  return 1; // No adjustment if starting at full retirement age
};

export const calculateSocialSecurity = (birthYear, retirementYear, pia, deathYear = null, projectionEndYear = null, ssColaRate = 0, applySsCola = false) => {
  // Get the year the person turns 62 (first eligible for SS)
  const eligibilityYear = birthYear + 62;
  
  // SS starts at eligibility year or retirement year, whichever is later
  // Note: 'retirementYear' here means the year SS benefits are claimed, not FERS retirement year.
  const startYear = Math.max(eligibilityYear, retirementYear);
  
  // Calculate the age when starting Social Security
  const startAge = startYear - birthYear;
  
  // Apply early retirement reduction or delayed retirement credit
  const fullRetirementAge = getFullRetirementAge(birthYear);
  const adjustmentFactor = getSocialSecurityAdjustmentFactor(startAge, fullRetirementAge);
  
  // Adjust the PIA by the adjustment factor
  const adjustedPia = pia * adjustmentFactor;
  
  const ssData = {};
  
  // Social Security continues until death date (if set), or projectionEndYear (if set), 
  // otherwise default to a reasonable projection (e.g., 40 years from start or age 100)
  const endCalcYear = deathYear || projectionEndYear || Math.max(startYear + 40, birthYear + 100);

  // Fill in the data for each year, applying COLA if enabled
  let currentBenefit = adjustedPia;
  for (let year = startYear; year <= endCalcYear; year++) {
    ssData[year] = currentBenefit;
    
    // Apply COLA for next year if enabled and not the last year
    if (applySsCola && ssColaRate > 0 && year < endCalcYear) {
      currentBenefit = currentBenefit * (1 + ssColaRate / 100);
    }
  }
  
  return ssData;
};

// Calculate Social Security Benefit based on Primary Insurance Amount (PIA) and claiming age
export const calculateSocialSecurityBenefit = (primaryInsuranceAmount, claimingAge, dateOfBirth) => {
  if (!primaryInsuranceAmount || !claimingAge || !dateOfBirth) return 0;
  
  const birthYear = new Date(dateOfBirth).getFullYear();
  
  // Full retirement age (FRA) based on birth year
  let fullRetirementAge = 66;
  if (birthYear >= 1960) {
    fullRetirementAge = 67;
  } else if (birthYear > 1954) {
    fullRetirementAge = 66 + ((birthYear - 1954) * 2 / 12);
  }
  
  // Calculate benefit adjustment based on claiming age
  let benefitAdjustment = 1.0; // Default at full retirement age
  
  if (claimingAge < fullRetirementAge) {
    // Early retirement reduction: 5/9 of 1% for first 36 months + 5/12 of 1% for additional months
    const monthsEarly = (fullRetirementAge - claimingAge) * 12;
    const firstReduction = Math.min(36, monthsEarly) * (5/9) * 0.01;
    const secondReduction = Math.max(0, monthsEarly - 36) * (5/12) * 0.01;
    benefitAdjustment = 1.0 - (firstReduction + secondReduction);
  } else if (claimingAge > fullRetirementAge) {
    // Delayed retirement credit: 8% per year after FRA up to age 70
    const yearsDelayed = Math.min(3, claimingAge - fullRetirementAge);
    benefitAdjustment = 1.0 + (yearsDelayed * 0.08);
  }
  
  return Math.round(primaryInsuranceAmount * benefitAdjustment);  
};

// Find the function that calculates retirement results - likely calculateResults or calculateScenario
// Add the FERS supplement calculation in the appropriate place where other income sources are calculated

export const calculateAdditionalIncomeData = (years, additionalIncomes = []) => {
  if (!years || years.length === 0) return [];

  // Convert all years to numbers for consistent comparison
  const numericYears = years.map(y => parseInt(y, 10));
  
  // console.log('[calculateAdditionalIncomeData] Input years:', numericYears);
  // console.log('[calculateAdditionalIncomeData] Additional incomes:', additionalIncomes);

  const additionalIncomeResults = numericYears.map((year, yearIndex) => {
    let yearlyTotalAdditionalIncome = 0;

    additionalIncomes.forEach((income, incomeIndex) => {
      // Parse all numeric values to ensure consistent types
      const startYear = parseInt(income.startYear, 10);
      const endYear = income.endYear ? parseInt(income.endYear, 10) : null;
      const amount = parseFloat(income.amount) || 0;
      const increaseRate = parseFloat(income.increaseRate) || 0;

      // Debug log for each income source being processed
      // console.log(`[Income ${incomeIndex}] Processing year ${year} (start: ${startYear}, end: ${endYear || 'none'})`);

      // Skip if year is before start year or after end year (if end year is set)
      if (year < startYear) {
        // console.log(`[Income ${incomeIndex}] Skipping - year ${year} is before start year ${startYear}`);
        return;
      }
      
      if (endYear !== null && year > endYear) {
        // console.log(`[Income ${incomeIndex}] Skipping - year ${year} is after end year ${endYear}`);
        return;
      }

      let currentYearIncomeAmount = 0;

      if (income.frequency === 'one-time') {
        if (year === startYear) {
          // console.log(`[Income ${incomeIndex}] Applying one-time income of ${amount} in year ${year}`);
          currentYearIncomeAmount = amount;
        } else {
          // console.log(`[Income ${incomeIndex}] Not applying one-time income - not the start year (current: ${year}, start: ${startYear})`);
        }
      } else if (income.frequency === 'ongoing') {
        // console.log(`[Income ${incomeIndex}] Processing ongoing income for year ${year}, startYear: ${startYear}, endYear: ${endYear || 'none'}`);
        
        // Base amount for the current year
        let yearAmount = amount;
        
        // Apply increases for years after the start year
        if (year > startYear) {
          if (income.increasingType === 'percentage') {
            const yearsOfIncrease = year - startYear;
            yearAmount = amount * Math.pow((1 + increaseRate / 100), yearsOfIncrease);
            // console.log(`[Income ${incomeIndex}] Applied ${increaseRate}% increase for ${yearsOfIncrease} years: ${amount} -> ${yearAmount}`);
          } else if (income.increasingType === 'flat') {
            const yearsOfIncrease = year - startYear;
            yearAmount = amount + (increaseRate * yearsOfIncrease);
            // console.log(`[Income ${incomeIndex}] Applied $${increaseRate} flat increase for ${yearsOfIncrease} years: ${amount} -> ${yearAmount}`);
          } else {
            // console.log(`[Income ${incomeIndex}] No increase applied (type: ${income.increasingType})`);
          }
        } else {
          // console.log(`[Income ${incomeIndex}] First year (${year}) - using base amount: ${amount}`);
        }
        
        currentYearIncomeAmount = yearAmount;
      } else {
        // console.log(`[Income ${incomeIndex}] Unknown frequency: ${income.frequency}`);
      }
      
      // console.log(`[Income ${incomeIndex}] Adding ${currentYearIncomeAmount} to year ${year} total`);
      yearlyTotalAdditionalIncome += currentYearIncomeAmount;
    });
    
    // console.log(`[Year ${year}] Total additional income: ${yearlyTotalAdditionalIncome}`);
    return Math.round(yearlyTotalAdditionalIncome);
  });

  // console.log('[calculateAdditionalIncomeData] Final results:', additionalIncomeResults);
  return additionalIncomeResults;
};

// Helper function to find the index for a specific age in the years array
const findAgeIndex = (targetYear, yearsArray) => {
  return yearsArray.findIndex(year => year === targetYear) !== -1 ? 
    yearsArray.findIndex(year => year === targetYear) : 0;
};

// For example, in the calculateRetirementResults function or similar:
export const calculateRetirementResults = (scenario, bioData, settings) => {
  // Generate years array from start year to end year
  const startYear = new Date().getFullYear();
  const endYear = startYear + 40; // Project 40 years by default
  const years = Array.from({length: endYear - startYear + 1}, (_, i) => startYear + i);
  
  // Default values for undefined variables
  const high3Salary = scenario.high3Salary || 0;
  const salaryData = scenario.salaryData || [];
  const socialSecurityData = scenario.socialSecurityData || [];
  const additionalIncomes = scenario.additionalIncomes || []; // Get additional incomes
  
  // Calculate pension data
  const pensionData = scenario.pensionData || [];
  
  // Calculate FERS supplement
  let supplementData = [];
  
  if (bioData.dateOfBirth) {
    const birthYear = new Date(bioData.dateOfBirth).getFullYear();
    const retirementAge = scenario.retirementYear - birthYear;
    
    if (isEligibleForSupplement(
      retirementAge, 
      scenario.yearsOfService, 
      scenario.pensionType || 'regular'
    )) {
      // Get estimated Social Security at age 62
      const estimatedSSAt62 = socialSecurityData && socialSecurityData.length > 0
        ? socialSecurityData[findAgeIndex(birthYear + 62, years)]
        : (high3Salary || (salaryData.length > 0 ? salaryData[salaryData.length - 1] : 0)) * 0.4;
      
      // Use the correct supplement calculation function based on retirement type
      if (scenario.pensionType === 'vera') {
        // For VERA, use the VERA-specific calculation that starts at MRA
        supplementData = calculateVeraFersSupplement(
          years,
          birthYear,
          scenario.retirementYear,
          scenario.yearsOfService,
          estimatedSSAt62
        );
      } else {
        // For all other retirement types, use the standard calculation
        supplementData = calculateFersSupplement(
          years,
          birthYear,
          scenario.retirementYear,
          scenario.yearsOfService,
          estimatedSSAt62
        );
      }
    } else {
      // Fill with zeros if not eligible
      supplementData = years.map(() => 0);
    }
  } else {
    // No DOB, fill with zeros
    supplementData = years.map(() => 0);
  }
  
  // Calculate additional income data
  const additionalIncomeData = calculateAdditionalIncomeData(years, additionalIncomes);

  // Add the supplement and additional income data to the results
  return {
    high3Salary,
    pensionData,
    supplementData,
    additionalIncomeData // Add new data here
  };
};

// If there's a separate function that calculates all income sources combined, 
// make sure supplement is included there as well